<template>
  <div
    v-if="visible"
    class="top-bar"
  >
    <transition
      name="fade-spoiler"
      mode="out-in"
    >
      <div
        v-if="isHidden"
        key="spoiler"
        class="top-bar__spoiler"
        :style="spoilerForeground"
        @click="onClick"
      />
      <a
        v-else
        key="content"
        :target="bannerItem?.target"
        :href="bannerItem?.url"
        :style="contentForeground"
        class="top-bar__content"
        @click="onClickBanner"
      >
        <button
          type="button"
          class="top-bar__close"
          @click.prevent="close"
        >
          <f-sprite-icon :icon-link="$options.icons.IconClose" />
        </button>
      </a>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue';

import IconClose from '@icons/close.svg';

import { useBanner } from '@/composables/useBanner';
import { IAdaptive } from '@/utils/system/adaptive';

export default defineComponent({
  name: 'top-bar',

  icons: {
    IconClose,
  },

  inject: ['adaptive'],

  setup() {
    const adaptive = inject<IAdaptive>('adaptive');
    const banner = useBanner();

    return {
      adaptive,
      banner,
    };
  },

  data() {
    return {
      isHidden: true,
    };
  },

  computed: {
    bannerData() {
      return this.banner.getFirstBanner('header_spoiler');
    },
    bannerItem() {
      const layout = this.adaptive?.isMobile ? 'mobile' : 'desktop';
      const data = this.bannerData.content?.[layout];
      if (typeof data === 'object') {
        return data.body;
      }
      return {};
    },
    spoilerForeground() {
      const img = this.bannerItem?.svg;
      return img ? `background-image: url(${img})` : '';
    },
    contentForeground() {
      const img = this.bannerItem?.hoverSvg;
      return img ? `background-image: url(${img})` : '';
    },
    visible() {
      return !!Object.keys(this.bannerItem || {}).length;
    },
  },

  methods: {
    onClick() {
      this.isHidden = false;
    },
    onClickBanner() {
      this.$persEvents.sendBannersClickEvent({
        id: this.bannerData?.id,
        slotId: 'header_spoiler',
      });
    },
    close() {
      this.isHidden = true;
    },
  },

  serverPrefetch() {
    return this.banner.loadBanners({ slots: ['header_spoiler'], withCategory: false });
  },
});
</script>

<style lang="scss">
.top-bar {
  &__spoiler,
  &__content {
    background-repeat: no-repeat;
    background-position: center;
    cursor: pointer;
  }

  &__spoiler {
    height: 32px;
    min-height: 32px;
    background-color: $blue;
    background-size: auto 32px;

    @include breakpoint("tablet") {
      height: 40px;
      background-size: auto 40px;
    }
  }

  &__content {
    background-color: $white;
    height: 120px;
    display: block;
    background-size: auto 120px;

    @include breakpoint("tablet") {
      height: 240px;
      background-size: auto 240px;
    }
  }

  &__close {
    cursor: pointer;
    color: $black;
    opacity: .4;
    background: none;
    padding: 0;
    position: absolute;
    right: 14px;
    top: 14px;
    width: 16px;
    height: 16px;

    @include breakpoint("tablet") {
      right: 26px;
      top: 26px;
      width: 20px;
      height: 20px;
    }

    &::after {
      content: "";
      position: absolute;
      left: -8px;
      right: -8px;
      top: -8px;
      bottom: -8px;
    }
  }
}

.top-bar__spoiler.fade-spoiler {
  &-leave-active {
    transition: opacity .4s ease, height .4s ease;
    height: 32px;

    @include breakpoint("tablet") {
      height: 40px;
    }
  }

  &-leave-to {
    height: 120px;

    @include breakpoint("tablet") {
      height: 240px;
    }
  }

  &-enter-active {
    transition: opacity .2s ease, height .2s ease;
    height: 120px;

    @include breakpoint("tablet") {
      height: 240px;
    }
  }

  &-enter-to {
    height: 32px;

    @include breakpoint("tablet") {
      height: 40px;
    }
  }

  &-enter,
  &-leave-active {
    opacity: 0;
  }
}

.top-bar__content.fade-spoiler {
  &-leave-active {
    transition: opacity .2s ease;
  }

  &-enter-active {
    transition: opacity .4s ease;
  }

  &-enter,
  &-leave-active {
    opacity: 0;
  }
}
</style>
